import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listTestimonials, createTestimonial, patchTestimonial, deleteTestimonial } from "@/lib/adminApi";
import { validateTestimonial, testimonialPayload, apiErrorText } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const EMPTY = { name: "", company: "", quote: "", active: true, sort_order: "0" };

export default function Testimonials() {
  useNoindex();
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try { setRows(await listTestimonials()); } catch (e) { toast.error(apiErrorText(e, "Yorumlar yüklenemedi")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const errs = validateTestimonial(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await createTestimonial(testimonialPayload(form));
      toast.success("Yorum eklendi");
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(apiErrorText(err));
    } finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try { await patchTestimonial(t.id, { active: !t.active }); await load(); }
    catch (e) { toast.error(apiErrorText(e)); }
  };
  const confirmDelete = async () => {
    const id = toDelete;
    setToDelete(null);
    try { await deleteTestimonial(id); toast.success("Silindi"); await load(); }
    catch (e) { toast.error(apiErrorText(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="mx-auto max-w-5xl px-4 py-6">
      <h1 className="mb-1 text-xl font-semibold">Müşteri yorumları</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Sadece gerçek, eski müşterilerinizden aldığınız yorumları buraya girin — ön raporun altında gösterilir.
      </p>

      <form onSubmit={create} noValidate className="mb-8 grid gap-3 rounded-md border p-4 sm:grid-cols-2" data-testid="testimonial-form">
        <div className="space-y-1.5">
          <Label htmlFor="t-name">Ad</Label>
          <Input id="t-name" value={form.name} onChange={set("name")} placeholder="Ismail K." maxLength={100} />
          {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="t-company">Firma (opsiyonel)</Label>
          <Input id="t-company" value={form.company} onChange={set("company")} placeholder="Beispiel GmbH" maxLength={150} />
          {errors.company && <p className="text-xs text-red-600">{errors.company}</p>}
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="t-quote">Yorum metni</Label>
          <Textarea id="t-quote" value={form.quote} onChange={set("quote")} maxLength={500} rows={3} />
          {errors.quote && <p className="text-xs text-red-600">{errors.quote}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="t-order">Sıra (küçük önce)</Label>
          <Input id="t-order" inputMode="numeric" value={form.sort_order} onChange={set("sort_order")} />
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Aktif
          </label>
          <Button type="submit" disabled={busy}>Ekle</Button>
        </div>
      </form>

      {rows === null && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">Henüz yorum yok.</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad</TableHead><TableHead>Firma</TableHead><TableHead>Yorum</TableHead>
                <TableHead>Sıra</TableHead><TableHead>Aktif</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid="testimonial-row">
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.company || "—"}</TableCell>
                  <TableCell className="max-w-xs text-sm">{t.quote}</TableCell>
                  <TableCell>{t.sort_order}</TableCell>
                  <TableCell><Switch checked={!!t.active} onCheckedChange={() => toggleActive(t)} aria-label="Aktif" /></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => setToDelete(t.id)}>Sil</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Yorum silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu yorum kalıcı olarak silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
